
<template>
  <div>
    <div>
      <section-header v-if="!isCardView"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :canAdd="canAdd"
                      :addLabel="addGestionnaire"
                      :fiche=fiche
                      @extended=setExtended
                      @closeDialog=closeDialog
                      @callback="createFraction"
                      ref="ETABLISSEMENT_GESTIONNAIRES">
      </section-header>
    </div>

    <div v-if="extended && !isCardView">
      <v-table class="fraction-dsn-table" dense>
        <thead class="pb-1">

          <tr class="font-weight-bold">
            <td v-for="(header, index) in headers" :key="index" style="vertical-align: bottom; font-size: 14px; border-bottom: thin solid rgba(0, 0, 0, 0.12);">
              {{ header }}
            </td>
            <td width="2%"></td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(fraction, index) in gestionnaires" :key="index">
            <td class="edit-field text-left py-3">
              <v-row class="justify-start">
                <div v-if="canMoveUp(index)" class="d-flex justify-center" @click.stop="moveUp(index)">
                  <v-icon size="23" color="#e42125">fas fa-chevron-up</v-icon>
                </div>
                <div v-if="canMoveDown(index)" class="d-flex justify-center" @click.stop="moveDown(index)">
                  <v-icon size="23" color="#e42125">fas fa-chevron-down</v-icon>
                </div>
              </v-row>
            </td>
            <td class="edit-field text-left">{{ fraction.fraction }} / {{ denominateurDsn }}</td>
            <td class="edit-field text-left py-3">{{ fraction.entite }}</td>
            <td class="edit-field text-left">{{ getType(fraction) }}</td>
            <td class="edit-field text-left">{{ getContact(fraction) }}</td>
            <td class="edit-field text-left">{{ fraction.mail }}</td>
            <td>
              <div v-if="canEdit(fraction)" class="d-flex">
                <div class="d-flex justify-center" @click.stop="updateFraction(fraction)">
                  <v-icon color="#2a97da" size="23">fas fa-pencil</v-icon>
                </div>
                <div v-if="canDelete(fraction)" class="d-flex justify-center" @click.stop="deleteFraction(fraction)">
                  <v-icon color="#e42125" size="23">fas fa-trash-can</v-icon>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>
    </div>
    <div v-else>
      <div class="card-list-container pt-4">
        <div v-for="(item, index) in gestionnaires" :key="index" class="pb-2">
          <v-row @click="selectItemCard(item)" class="list-card">
            <v-col>
              <v-row class="ma-0">
                <div class='list-logo list-logo-initial'>
                  <div>{{ getInitials(item) }}</div>
                </div>
                <div style="padding-left: 12px;">
                  <div class="card-title">
                    {{ getTitle(item.entite) }}
                  </div>
                  <div class="card-sub-title">
                    {{ getType(item) }}
                  </div>
                </div>
              </v-row>
              <div class="seperator" />

              <v-row class="ma-0">
                <div class="info-box">
                  <div class="info-header">Fraction</div>
                  <div class="info-text">{{ item.fraction }} / {{ denominateurDsn }}</div>
                </div>
                <div class="info-box">
                  <div class="info-header">Contact</div>
                  <div class="info-text">
                    {{ getContact(item) }}
                    <v-icon class="copy-text-icon" @click.stop="copyText(getContact(item), 0)">{{ copiedField === 0 ? 'fas fa-check' : 'fas fa-copy' }}</v-icon>
                  </div>
                </div>
                <div class="info-box">
                  <div class="info-header">Mail</div>
                  <div class="info-text">
                    {{ item.mail }}
                    <v-icon class="copy-text-icon" @click.stop="copyText(item.mail, 1)">{{ copiedField === 1 ? 'fas fa-check' : 'fas fa-copy' }}</v-icon>
                  </div>
                </div>
              </v-row>
            </v-col>
            <v-col cols="1" class="align-content-center align-items-center">
              <v-icon class="chevron-right">
                fas fa-chevron-right
              </v-icon>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>

    <div class="pt-3" v-if="canAdd && isCardView">
      <v-btn class="base"
             :outlined="true"
             :loading="loading"
             :block="true"
             variant="flat"
             @click="createFraction">
        {{ addGestionnaire }}
      </v-btn>
    </div>

    <div v-if="extended && !isCardView" class="d-flex flex-row-reverse">
      <loader-button class="ms-2"
                     :label="wizardButtonLabel"
                     :loading="loading"
                     :disabled="!canSave"
                     :full-width="true"
                     @callback="wizardButtonAction">
      </loader-button>
    </div>

    <section-container v-if="dialogOpen" :section="section"
                       :dialogOpen="dialogOpen"
                       :extended="true"
                       :fiche="fiche"
                       @closeDialog="closeSection"
                       @saved="saved">
    </section-container>

  </div>
</template>

<script lang="ts">
  import { GenSection, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import {
    FwkButtons,
    RessourceFwkButton,
    RessourceFwkSection,
    FwkSections,
    AppCustoms,
    RessourceAppCustom,
    RessourceAppSection, AppSections
  } from '@/ressources/globalRessources';
  import Section from '@/framework/base/classes/Section';
  import Etablissement from '@/generated/classes/Etablissement';
  import SectionFractionDsn from '@/extend/sections/SectionFractionDsnExtend.vue';
  import SectionContainer from '@/framework/base/components/SectionContainer.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import { storeSession, storeEtablissement, storeFractionDsn } from '@/generated/stores';
  import { Extended } from '@/framework/base/classes/Schema';
  import FractionDsn, { FractionDsnGenres } from '@/generated/classes/FractionDsn';
  import { isMobile } from '@/style/StyleHelpers';
  import { Alerts } from '@/framework/base/helpers/Alerts';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import UserProfile from "@/framework/base/classes/UserProfile";
  import { FieldStatus } from '@/framework/base/components/FieldInfo';
  import { Fiches } from '@/generated/classes/AppSchema';
  import { FractionDsnTypes } from '@/generated/classes/FractionDsn';
  import { defineComponent, type PropType } from "vue";
  import ListHeader from "@/framework/base/components/ListHeader.vue";
  import HubServices from '@/generated/hub/HubServices';

  export default defineComponent({
    components: {
      ListHeader,
      SectionContainer,
      SectionHeader,
      LoaderButton
    },
    props: {
      etablissement: {
        type: Object as PropType<Etablissement>,
        required: true
      },
      dialog: {
        type: Boolean
      },
      extended: {
        type: Boolean
      },
      isAbstract: {
        type: Boolean
      }
    },
    created() {
      HubServices.onEtablissementFractionUpdated(this.configureFractions);
      this.toConfigure = this.refEtablissement.fractionDsn.find(a => a.genre == FractionDsnGenres.AGETA) != undefined;
    },
    beforeUnmount() {
      if (this.canSave && this.refEtablissement.fractionDsn.length > 0) {
        this.dialogSaveOrCancel();
      }
    },
    data() {
      return {
        isMobile: isMobile(),
        dialogOpen: false,
        loading: false,
        editedInSection: false,
        toConfigure: false,
        selectedFraction: new FractionDsn(),
        cloneEtablissement: new Etablissement(),
        status: new FieldStatus(false, false, 0),
        fiche: Fiches.ETABLISSEMENT,
        refEtablissement: this.etablissement,
        copiedField: null as number | null,
        isCardView: true
      };
    },
    mounted() {
      if (this.isAbstract && this.gestionnaires.length == 0) {
        this.createFraction();
      }
    },
    computed: {
      ressourceContact() {
        return RessourceAppCustom(AppCustoms.CONTACT).label;
      },
      ressourceCoord() {
        return RessourceAppCustom(AppCustoms.COORD).label;
      },
      ressourcePrestataire() {
        return RessourceAppCustom(AppCustoms.PRESTATAIRE).label;
      },
      ressourceRang() {
        return RessourceAppCustom(AppCustoms.RANG).label;
      },
      saveLabel() {
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },
      errorTxt() {
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },
      ressource() {
        return RessourceGenSection(GenSection.GESTIONNAIRES_PAIE);
      },
      addGestionnaire() {
        return RessourceFwkSection(FwkSections.NEW_GESTIONNNAIRE).label;
      },
      wizardButtonLabel() {
        if (this.gestionnaires.length == 0 && !this.editedInSection) {
          return RessourceFwkSection(FwkSections.CONFIGURE_GESTIONNNAIRE).label;
        } else {
          return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
        }
      },
      headers() {
        if (this.isMobile) {
          return [this.ressourceRang, this.ressourcePrestataire, 'type', this.ressourceContact, 'mail'];
        } else {
          return [this.ressourceRang, this.ressourcePrestataire, 'type', this.ressourceContact, 'mail'];
        }
      },
      configured(): boolean {
        return this.refEtablissement.fractionDsn.find(a => a.genre == FractionDsnGenres.AGETA) != undefined;
      },
      gestionnaires(): FractionDsn[] {
        if (this.refEtablissement.id != this.cloneEtablissement.id || this.toConfigure != this.configured) {
          this.setToconfigure();
          storeFractionDsn.mFractionDsnClearData();
          const storeFractions: FractionDsn[] = [];;
          this.refEtablissement.fractionDsn.forEach((fraction) => {
            storeFractions.push(FractionDsn.fromJson(fraction));
          });
          storeFractionDsn.mFractionDsnLoaded(storeFractions);
          this.setCloneEtablissement();
        }
        return storeFractionDsn.listFractionDsn.filter(a => a.id > 0 && a.etablissementId == this.refEtablissement.id).sort((a, b) => a.fraction - b.fraction);
      },
      denominateurDsn(): number {
        const ceaTese = this.refEtablissement.fractionDsn.find(a => a.genre == FractionDsnGenres.TESE_CEA);
        if (ceaTese) {
          return 9;
        } else {
          return this.gestionnaires.length;
        }
      },
      canAdd(): boolean {
        return this.refEtablissement.fractionDsn.length >= 0;
      },
      canSave(): boolean {
        if (this.refEtablissement.fractionDsn.length == 0) {
          return true;
        } else if (this.editedInSection) {
          const current = JSON.stringify(this.refEtablissement.fractionDsn);
          const clone = JSON.stringify(this.cloneEtablissement.fractionDsn);
          this.setStatusUpdated(current != clone);
          return current != clone;
        } else {
          return false;
        }
      },
      section(): Section {
        const section = new Section();
        section.component = SectionFractionDsn;
        section.type = 'fractionDsn';
        section.data = this.selectedFraction;
        section.ressource = this.ressource;
        section.propData = this.refEtablissement;
        section.propType = 'etablissement';
        return section;
      },
      isSupport(): boolean {
        let res = false;
        const user = storeSession.connectedUser
        if (user && user.id > 0) {
          res = (user.profil & UserProfile.userInterne) > 0;
        }
        return res;
      }
    },

    methods: {
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      setCloneEtablissement() {
        this.cloneEtablissement = this.refEtablissement.cloneEtablissement();
      },
      setToconfigure() {
        this.toConfigure = this.configured;
      },
      configureFractions() {
        this.toConfigure = !this.configured;
      },
      getType(fraction: FractionDsn) {
        let type = 'Régime général';
        if (fraction.type == FractionDsnTypes.INTERMITTENTS) {
          type = 'Intermittents';
        } else if (fraction.type == FractionDsnTypes.MIXTE) {
          type = 'Régime général et intermittents';
        }
        return type;
      },
      getContact(fraction: FractionDsn) {
        return fraction.prenom + ' ' + fraction.nom;
      },
      saved() {
        this.refEtablissement.fractionDsn = storeFractionDsn.listFractionDsn;
        this.refEtablissement.fractionDsn.forEach((fraction, index) => {
          fraction.fraction = index + 1;
          fraction.id = index + 1;
        });
        this.update();
      },
      wizardButtonAction(): void {
        if (this.gestionnaires.length == 0 && !this.editedInSection) {
          this.setFractionDsn();
        } else {
          this.update()
        }
      },
      update(): void {
        this.loading = true;
        storeEtablissement.updateEtablissement(this.refEtablissement);
        this.cloneEtablissement = this.refEtablissement.cloneEtablissement();
        this.editedInSection = false;
        this.loading = false;
      },
      setFractionDsn(): void {
        this.loading = true;
        Etablissement.setFractionDsn(this.refEtablissement).then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      },
      createFraction(): void {
        this.editedInSection = true;
        this.selectedFraction = storeFractionDsn.getFractionDsn(0);
        this.selectedFraction.fraction = this.gestionnaires.length;
        this.selectedFraction.etablissementId = this.refEtablissement.id;
        if (this.isAbstract) {
          this.$emit("openCrud", this.section);
        } else {
          this.dialogOpen = true;
        }
      },
      updateFraction(fraction: FractionDsn): void {
        this.editedInSection = true;
        this.selectedFraction = storeFractionDsn.getFractionDsn(fraction.id);
        this.dialogOpen = true;
      },
      canEdit(fraction: FractionDsn): boolean {
        return fraction.genre != FractionDsnGenres.AGETA;
      },
      canDelete(fraction: FractionDsn): boolean {
        return fraction.genre != FractionDsnGenres.AGETA;
      },
      deleteFraction(fraction: FractionDsn): void {
        Etablissement.deleteFractionDsn(fraction).then((res) => {
          if (res) {
            this.toConfigure = !this.configured;
          }
        });
      },
      dialogSaveOrCancel(): Promise<boolean> {
        return new Promise((resolve) => {
          Alerts.saveOrCancel()
            .then((result: boolean) => {
              if (result) {
                this.update();
              } else {
                storeFractionDsn.mFractionDsnClearData();
                const storeFractions: FractionDsn[] = [];;
                this.cloneEtablissement.fractionDsn.forEach((fraction) => {
                  storeFractions.push(FractionDsn.fromJson(fraction));
                });
                storeFractionDsn.mFractionDsnLoaded(storeFractions);
                this.refEtablissement.fromEtablissement(this.cloneEtablissement);
              }
              resolve(result);
            });
        });
      },
      closeSection(): void {
        storeFractionDsn.listFractionDsn.forEach((item) => {
          if (item.id < 0) {
            storeFractionDsn.mFractionDsnDeleted(item.id);
          }
        })
        this.dialogOpen = false;
      },
      closeDialog(): void {
        this.$emit('closeDialog');
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },
      copyText(text: string, fieldIndex: number): void {
        const textToCopy = text.trim();
        navigator.clipboard.writeText(textToCopy).then(() => {
          this.copiedField = fieldIndex;
          setTimeout(() => this.copiedField = null, 1500);
        }).catch((err) => {
          console.error('Failed to copy:', err);
        });
      },
      getInitials(obj: FractionDsn): string {
        const name = obj.entite;
        const initials = name.split(/[ -]+/);
        if (initials.length > 1) {
          return initials[0][0] + initials[initials.length - 1][0];
        } else {
          return initials[0][0] + initials[0][1];
        }
      },
      selectItemCard(fraction: any): void {
        this.editedInSection = true;
        this.selectedFraction = storeFractionDsn.getFractionDsn(fraction.id);
        if (this.isAbstract) {
          this.$emit("openCrud", this.section);
        } else {
          this.dialogOpen = true;
        }
      },
      getTitle(value: any): string {
        return value;
      },
      moveUp(index: number): void {
        const toIndex = index - 1
        if (toIndex < 0) return

        const a = this.gestionnaires[index]
        const b = this.gestionnaires[toIndex]

        const tmp = a.fraction
        a.fraction = b.fraction
        b.fraction = tmp

        this.editedInSection = true;
      },

      moveDown(index: number): void {
        const toIndex = index + 1
        if (toIndex >= this.gestionnaires.length) return

        const a = this.gestionnaires[index]
        const b = this.gestionnaires[toIndex]

        const tmp = a.fraction
        a.fraction = b.fraction
        b.fraction = tmp

        this.editedInSection = true;
      },

      canMoveUp(index: number): boolean {
        if (index == 0) {
          return false;
        } else {
          return true;
        }
      },

      canMoveDown(index: number): boolean {
        if (this.gestionnaires.length - 1 == index) {
          return false;
        } else {
          return true;
        }
      }
    }
  })

</script>